import { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useDashboard } from '@/hooks/useDashboard';
import { dashboardService } from '@/services/dashboardService';
import { useIsMobile } from '@/hooks/useIsMobile';

// 全局筛选维度：战区在前，剔除一级品类/品牌/一级系列
const FIELD_ORDER = [
  '战区',
  '省办',
  '营业所',
  '客户',
  '人员',
  '渠道',
  '年',
  '月',
];

/** 维度显示标签 */
const FIELD_LABEL: Record<string, string> = {
  战区: '战区',
  省办: '省办',
  营业所: '营业所',
  客户: '客户',
  人员: '人员',
  渠道: '渠道',
  年: '年',
  月: '月',
};

/** 下拉最多渲染的选项数，超出提示搜索缩小范围（客户/人员基数大） */
const MAX_VISIBLE = 200;

type SelectedMap = Record<string, string[]>;

interface FilterPanelProps {
  open: boolean;
  onClose?: () => void;
}

interface FilterSelectProps {
  field: string;
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  isMobile?: boolean;
  remote?: boolean;
}

/** 高基数维度，通过后端 LIKE 匹配动态拉取候选值 */
const REMOTE_FIELDS = new Set(['客户', '人员']);

/** 单个字段的下拉多选：搜索 + 点选 + 已选标签；高基数字段支持远程搜索 */
function FilterSelect({ field, label, options, selected, onChange, isMobile, remote }: FilterSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [remoteOptions, setRemoteOptions] = useState<string[]>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const isRemote = !!remote;

  // 点击下拉外部时关闭
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
        setRemoteOptions([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // 远程搜索：输入关键字防抖请求后端
  useEffect(() => {
    if (!isRemote) return;

    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    const trimmed = query.trim();
    if (!trimmed) {
      setRemoteOptions([]);
      setRemoteLoading(false);
      return;
    }

    setRemoteLoading(true);
    searchTimerRef.current = window.setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      dashboardService
        .searchFilterOptions(field, trimmed, controller.signal)
        .then((vals) => {
          if (!controller.signal.aborted) setRemoteOptions(vals);
        })
        .catch(() => {
          if (!controller.signal.aborted) setRemoteOptions([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setRemoteLoading(false);
        });
    }, 300);

    return () => {
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [isRemote, field, query]);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const q = query.trim().toLowerCase();
  // 本地模式：在全局 options 中过滤；远程模式：直接显示后端返回的选项
  const localFiltered = q ? options.filter((v) => v.toLowerCase().includes(q)) : options;
  const visible = isRemote ? remoteOptions : localFiltered.slice(0, MAX_VISIBLE);
  const allSelected = options.length > 0 && selected.length === options.length;

  return (
    <div className="filter-item filter-select-wrap" ref={containerRef}>
      <label>
        {label}
        {!isRemote && options.length > 0 && <span className="filter-count">{options.length}</span>}
        {isRemote && <span className="filter-count remote">搜索</span>}
      </label>
      <div
        className="filter-select-box"
        onClick={() => {
          setOpen((v) => !v);
          setQuery('');
          setRemoteOptions([]);
        }}
      >
        {selected.length > 0 ? (
          <div className="filter-chips">
            {selected.slice(0, isMobile ? 3 : 6).map((v) => (
              <span
                key={v}
                className="chip"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(selected.filter((x) => x !== v));
                }}
              >
                {v}
                <span className="chip-x"><X size={10} /></span>
              </span>
            ))}
            {selected.length > (isMobile ? 3 : 6) && (
              <span className="chip chip-more">+{selected.length - (isMobile ? 3 : 6)}</span>
            )}
          </div>
        ) : (
          <span className="filter-placeholder">{isRemote ? `输入关键字搜索${label}…` : `点击选择${label}…`}</span>
        )}
        <span className="filter-caret">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
      </div>

      {open && (
        <>
          {isMobile && (
            <div
              className="filter-dropdown-backdrop"
              onClick={() => {
                setOpen(false);
                setQuery('');
                setRemoteOptions([]);
              }}
            />
          )}
          <div className="filter-dropdown">
            <div className="filter-dropdown-header">
              <span className="filter-dropdown-title">{label}</span>
              <button
                className="filter-dropdown-close"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  setQuery('');
                  setRemoteOptions([]);
                }}
                aria-label="关闭"
              >
                <X size={16} />
              </button>
            </div>
            <div className="filter-dropdown-tools">
              <input
                className="filter-search"
                autoFocus
                placeholder={`搜索${label}…`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {!isRemote && options.length <= 500 && (
                <button
                  className="filter-mini-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(allSelected ? [] : [...options]);
                  }}
                >
                  {allSelected ? '清空' : '全选'}
                </button>
              )}
            </div>
            <div className="filter-options">
              {isRemote && !query.trim() && (
                <div className="filter-empty">请输入{label}关键字进行搜索</div>
              )}
              {remoteLoading && <div className="filter-loading-inline">搜索中…</div>}
              {!remoteLoading && visible.length === 0 && (
                <div className="filter-empty">
                  {isRemote && query.trim() ? '无匹配项，换个关键字试试' : '无匹配项'}
                </div>
              )}
              {visible.map((v) => (
                <div
                  key={v}
                  className={`filter-option ${selected.includes(v) ? 'active' : ''}`}
                  onClick={() => toggle(v)}
                >
                  <span className="filter-check">{selected.includes(v) ? <Check size={14} /> : null}</span>
                  <span className="filter-option-text">{v}</span>
                </div>
              ))}
              {!isRemote && localFiltered.length > MAX_VISIBLE && (
                <div className="filter-more-hint">还有 {localFiltered.length - MAX_VISIBLE} 项，输入关键字缩小范围</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function FilterPanel({ open, onClose }: FilterPanelProps) {
  const { filters } = useDashboardStore();
  const { applyFilters } = useDashboard();
  const isMobile = useIsMobile();
  const [options, setOptions] = useState<Record<string, string[]>>({});
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selected, setSelected] = useState<SelectedMap>({});

  // 打开面板时拉取全局候选值（跨 tab 共享同一套筛选器内容）
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoadingOptions(true);
    setOptions({});
    dashboardService
      .fetchFilterOptions(undefined, controller.signal)
      .then((opts) => {
        if (!controller.signal.aborted) setOptions(opts);
      })
      .catch(() => {
        if (!controller.signal.aborted) setOptions({});
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingOptions(false);
      });
    return () => {
      controller.abort();
    };
  }, [open]);

  // 打开面板时，把 store 中已生效的全局筛选解析为选中状态
  useEffect(() => {
    if (!open) return;
    const init: SelectedMap = {};
    for (const key of FIELD_ORDER) {
      const raw = filters?.[key];
      if (raw) init[key] = raw.split(/\s+/).filter(Boolean);
    }
    setSelected(init);
  }, [open, filters]);

  /** 将当前 selected 解析为 DashboardFilters 并应用 */
  const applySelected = (nextSelected: SelectedMap) => {
    const out: Record<string, string> = {};
    for (const [key, vals] of Object.entries(nextSelected)) {
      if (vals.length === 0) continue;
      const totalOptions = options[key]?.length || 0;
      // 选中全部 = 不筛该字段（避免生成超长 OR 条件）
      if (totalOptions > 0 && vals.length === totalOptions) continue;
      out[key] = vals.join(' ');
    }
    applyFilters(out);
  };

  const handleFieldChange = (key: string, vals: string[]) => {
    setSelected((prev) => ({ ...prev, [key]: vals }));
  };

  const handleReset = () => {
    setSelected({});
    applyFilters({});
  };

  const handleApply = () => {
    applySelected(selected);
    onClose?.();
  };

  // 始终展示 v3 的全部筛选字段（无候选值时空选项，保留已选手动输入/缓存值）
  const fields = FIELD_ORDER;

  return (
    <div className={`filter-panel ${open ? 'open' : ''}`}>
      <div className="filter-panel-header">
        <span className="filter-panel-title">筛选条件</span>
        {onClose && (
          <button className="filter-panel-close" onClick={onClose} aria-label="收起筛选">
            <X size={18} />
          </button>
        )}
      </div>
      {loadingOptions ? (
        <div className="filter-loading">加载筛选选项…</div>
      ) : (
        <div className="filter-grid">
          {fields.map((key) => (
            <FilterSelect
              key={key}
              field={key}
              label={FIELD_LABEL[key] || key}
              options={options[key] || []}
              selected={selected[key] || []}
              onChange={(vals) => handleFieldChange(key, vals)}
              isMobile={isMobile}
              remote={REMOTE_FIELDS.has(key)}
            />
          ))}
        </div>
      )}
      <div className="filter-actions">
        <button className="btn-secondary" onClick={handleReset}>
          清空全部
        </button>
        {onClose && (
          <button className="btn-primary" onClick={handleApply}>
            应用筛选
          </button>
        )}
      </div>
      <div className="filter-hint">
        提示：① 选择筛选条件后点击「应用筛选」生效；② 筛选对所有页签的明细表格同时生效；③ 顶部 KPI/图表仍为全国口径。
      </div>
    </div>
  );
}
